/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { isPredecessorComplete, stringifyPredecessors, TaskRelationType } from '@gh/builder-shared-data';
import {
  createDialogRoute,
  EnumDictionary,
  FormState, FormStateBuilder,
  getDialogInitData,
  NumberTypes,
} from '@gh/common-shared';
import { sliceDataByQuery } from '@gh/core-data';
import { AutocompleteLoadMoreEvent, DialogInput, getControl } from '@gh/core-ui';
import { withoutIndex } from '@gh/core-util';
import { distinctUntilChanged$ } from '@gh/rx/operators';
import { isEmpty, isEqual, keyBy, last } from 'lodash';
import {
  createEmptyGanttPredecessorListItem,
  GanttPredecessorKitDetails, GanttPredecessorListItem,
  ganttPredecessorListItemFromRelation,
  ganttPredecessorListItemToRelation,
  GanttTaskRelation,
  getGanttScheduleIdFromId,
} from '../../resources/gantt.model';
import {
  GanttChartBusinessService,
  GanttPredecessorKitDialogConfig,
} from '../../services/gantt-chart-business.service';

@Component({
  selector: 'gh-predeceesor-kit-dialog',
  template: require('./gantt-predecessor-kit-dialog.component.html'),
  styles: [require('./gantt-predecessor-kit-dialog.component.scss')],
})
export class GanttPredecessorKitDialogComponent implements OnInit, DialogInput<GanttPredecessorKitDialogConfig> {
  input: GanttPredecessorKitDialogConfig;
  form: FormState<{ predecessors: GanttPredecessorListItem[] }>;
  relationshipTypeDict: EnumDictionary<TaskRelationType>;
  focusedPredecessorIndex: number;

  constructor(@Inject(MAT_DIALOG_DATA) private dialogData: any,
              private dialogRef: MatDialogRef<any>,
              private formStateBuilder: FormStateBuilder) {
  }

  get name(): string {
    return this.input.name;
  }

  get formula(): string {
    return stringifyPredecessors(this.getCompletedTaskRelations(), 1);
  }

  get canAddMore(): boolean {
    return isPredecessorComplete(last(this.getTaskRelations())!); // tslint:disable-line:no-non-null-assertion
  }

  get canApply(): boolean {
    return this.getTaskRelations().some(isPredecessorComplete);
  }

  ngOnInit(): void {
    const { relationshipTypeDict } = getDialogInitData(this.dialogData);
    const originalPredecessors = isEmpty(this.input.predecessors)
      ? [createEmptyGanttPredecessorListItem()]
      : this.input.predecessors.map(ganttPredecessorListItemFromRelation);

    this.relationshipTypeDict = relationshipTypeDict;

    this.form = this.formStateBuilder.buildFunctional(
      { predecessors: originalPredecessors },
      ({ predecessors }, { root, array, group }) => root({
        children: {
          predecessors: array({
            children: predecessors.map((predecessor) => group({
              key: predecessor.id.toString(),
              children: {
                id: void 0,
                task: void 0,
                relation: { required: true },
                lagType: void 0,
                lag: { type: NumberTypes.DurationZero },
              },
            })),
          }),
        },
      }));

    this.focusedPredecessorIndex = 0;

    this.form.valueChanges.pipe(
      distinctUntilChanged$(isEqual))
      .subscribe(() => this.validateRelationOnGanttChart());
  }

  loadTasks(event: AutocompleteLoadMoreEvent): void {
    event.load(sliceDataByQuery(
      this.input.findTasks(getGanttScheduleIdFromId(this.input.taskId), event.query.filter, this.input.taskId),
      event.query));
  }

  onAdd(): void {
    this.form.modifyValue(({ predecessors }) => ({
      predecessors: predecessors.concat([createEmptyGanttPredecessorListItem()]),
    }));

    this.focusedPredecessorIndex = this.form.value.predecessors.length - 1;
  }

  onRemove(index: number): void {
    this.form.modifyValue(({ predecessors }) => ({ predecessors: withoutIndex(predecessors, index) }));

    this.validateRelationOnGanttChart();
  }

  onApply(): void {
    this.validateRelationOnGanttChart();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close(this.getCompletedTaskRelations());
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private getPredecessorFormArray(): FormArray {
    return <FormArray>getControl(this.form.group, 'predecessors');
  }

  private getTaskRelations(): GanttTaskRelation[] {
    return this.getPredecessorFormArray().value.map(ganttPredecessorListItemToRelation);
  }

  private getCompletedTaskRelations(): GanttTaskRelation[] {
    return this.getTaskRelations().filter(isPredecessorComplete);
  }

  private validateRelationOnGanttChart(): void {
    const invalidRelations = this.input.validateRelations(
      this.input.taskId,
      this.getCompletedTaskRelations());
    const invalidRelationIndex = keyBy(invalidRelations, 'taskId');
    const predecessorArray = this.getPredecessorFormArray();
    predecessorArray.controls.forEach((group: FormGroup) => {
      const taskControl = getControl(group, 'task');
      if (taskControl.value && invalidRelationIndex[taskControl.value.id]) {
        taskControl.setErrors({ predecessorInvalidRelation: { messageId: 'msg_sch_cyclic_dependence' } });
      } else {
        taskControl.updateValueAndValidity();
      }
    });
  }

}

export const ganttPredecessorKitDialogRoute = createDialogRoute({
  component: GanttPredecessorKitDialogComponent,
  init: GanttChartBusinessService,
});
